// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialInvoiceState,
    InvoiceSchema,
    InvoiceFormFields,
    invoiceTypes,
} from "./Invoice.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";
import InvoiceLines from "./InvoiceLines";

const Invoice = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [invoiceId, setInvoiceId] = useState(undefined);
    const [invoice_header, setInvoice] = useState(InitialInvoiceState);
    const [formFields, setFormFields] = useState(InvoiceFormFields);
    const [schema, setSchema] = useState(InvoiceSchema);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [invoiceStatus, setInvoiceStatus] = useState(true);
    const [dependencies, setDependencies] = useState({
        schools: [],
        students: [],
        accounts: [],
        areas: [],
        terms: [],
        academic_years: [],
        transport_types: [],
        classes: [],
        zones: [],
    });
    const attachOptions = (name, options) => {
        setFormFields((prevFormFields) =>
            prevFormFields.map((field) =>
                field.name === name &&
                    (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleValueChange = (e, values) => {
        invoiceHeaderChangeHandler(e.target.name, e.target.value);
        return;
    };

    const handlePdfDownloadChange = async () => {
        const response = await fetch(
            gen_link(`${EntityConstants.PDF_INVOICE_DOWNLOAD}/${invoiceId}`)
        );
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, "_blank");

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.PDF_INVOICE_DOWNLOAD}/${invoiceId}`);
        // link.target = '_blank';
        // link.click();
    };

    const invoiceHeaderChangeHandler = (key, value) => {
        if (key === "invoice_status" && typeof value !== "boolean") {
            value = value === "true" ? false : true;
        } else if (key === "invoice_status" && typeof value === "boolean") {
            value = value ? false : true;
        }
        setInvoice((prevState) => {
            return { ...prevState, [key]: value };
        });
    };

    const getMonthEndDate = (month) => {
        const year = new Date().getFullYear();
        const day = new Date(`${month} 1, ${year}`);
        const nextMonth = new Date(
            day.getFullYear(),
            day.getMonth() + 1,
            0
        ).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
        return nextMonth;
    };

    // Fetch the created invoice header alone with invoice lines details
    const getInvoice = async (invoice_header_id) => {
        const response = await get(
            EntityConstants.INVOICE_HEADERS + `/${invoice_header_id}`
        );
        const data = await response.json();
        setInvoiceStatus(data.invoice_status);
        setInvoice(data);
        setInvoiceId(data.id);
        setInvoiceLines(data.invoice_lines);
    };

    // Fetch the created invoice lines from invoice header
    const getInvoiceLines = async (invoice_header_id) => {
        const response = await get(
            EntityConstants.INVOICE_HEADERS + `/${invoice_header_id}`
        );
        const data = await response.json();
        setInvoiceLines(data.invoice_lines);
    };

    // Submit the from values to create a invoice header
    const handleSubmit = async (values) => {
        spinService.on();
        const isNewInvoice = values.id === undefined;
        const url = isNewInvoice
            ? EntityConstants.INVOICE_HEADERS
            : EntityConstants.INVOICE_HEADERS + `/${values.id}`;
        const method = isNewInvoice ? post : put;
        const response = await method(url, { invoice_header: values });
        if ([200, 201].indexOf(response.status) > -1) {
            const data = await response.json();
            toast.success(
                isNewInvoice
                    ? MessageContants.INVOICE_HEADER_CREATED
                    : MessageContants.INVOICE_HEADER_UPDATED
            );
            setInvoiceId(data.id);
            getInvoice(data.id);
        }
        spinService.off();
    };

    useEffect(() => {
        const fetchInvoice = async (id) => {
            if (id === undefined) {
                setInvoice(invoiceTypes.Fees.state);
                setFormFields(invoiceTypes.Fees.formFields);
                setSchema(invoiceTypes.Fees.schema);
            } else {
                getInvoice(id);
            }
        };
        id && fetchInvoice(id);
        return () => {
            setSchema(InvoiceSchema);
        };
    }, [id]);

    // Load the Schools, and Transport Types
    useEffect(() => {
        const fetchSchoolsAndTransportTypes = async () => {
            let requests = [
                get(EntityConstants.SCHOOLSBYCOMPANIES),
                get(EntityConstants.TRANSPORT_TYPES, "max=all"),
                get(EntityConstants.ACADEMIC_YEARS, "max=all"),
            ];
            const responses = await Promise.all(requests);
            const [schools, transport_types, academic_years] = await Promise.all(
                responses.map((response) => response.json())
            );
            setDependencies((prevState) => {
                return {
                    ...prevState,
                    schools: schools,
                    transport_types: transport_types,
                    academic_years: academic_years
                };
            });
        };
        fetchSchoolsAndTransportTypes();
    }, [invoice_header.fee_type]);

    // Load Accounts
    useEffect(() => {
        const fetchAccounts = async () => {
            const response = await get(EntityConstants.ACCOUNTS, "max=all");
            const accounts = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, accounts: accounts };
            });
        };
        invoice_header.fee_type === "Fees" && fetchAccounts();
    }, [invoice_header.fee_type]);

    // Load Students and Academic Year
    useEffect(() => {
        const fetchStudentsAndAcademicYears = async (school_id) => {
            let requests = [
                get(EntityConstants.STUDENTS, `max=all&school_id=${school_id}`),
                get(EntityConstants.GRADES, `max=all&school_id=${school_id}`),
                get(EntityConstants.ZONES_BY_SCHOOL + `/${school_id}`),
            ];
            const responses = await Promise.all(requests);
            const [students, classes, zones] = await Promise.all(
                responses.map((response) => response.json())
            );
            setDependencies((prevState) => {
                return {
                    ...prevState,
                    students: students,
                    classes: classes,
                    zones: zones,
                };
            });
        };
        invoice_header.school_id &&
            invoice_header.fee_type === "Fees" &&
            fetchStudentsAndAcademicYears(invoice_header.school_id);
    }, [invoice_header.school_id]);

    // Load Terms
    useEffect(() => {
        const fetchTermsBySchoolAndAcademicYear = async (
            school_id,
            academic_year_id
        ) => {
            const response = await get(
                EntityConstants.TERMS,
                `school_id=${school_id}&academic_year_id=${academic_year_id}`
            );
            const terms = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, terms: terms };
            });
        };
        invoice_header.school_id &&
            invoice_header.academic_year_id &&
            fetchTermsBySchoolAndAcademicYear(
                invoice_header.school_id,
                invoice_header.academic_year_id
            );
    }, [invoice_header.school_id, invoice_header.academic_year_id]);

    // Set Post date based on the selected period
    useEffect(() => {
        const setPostDate = (period) => {
            invoiceHeaderChangeHandler("post_date", getMonthEndDate(period));
        };
        invoice_header.period && setPostDate(invoice_header.period);
    }, [invoice_header.period]);

    // Change Form based on the fee type
    useEffect(() => {
        const changeForm = (fee_type) => {
            if (id === undefined && invoice_header.id === undefined) {
                setInvoice(invoiceTypes[fee_type].state);
            }
            let formFields = invoiceTypes[fee_type].formFields;
            if (id !== undefined || invoice_header.id !== undefined) {
                formFields = invoiceTypes[fee_type].formFields.map((field) =>
                    field.name === "fee_type" ? { ...field, disabled: true } : field
                );
            }
            setFormFields(formFields);
            setSchema(invoiceTypes[fee_type].schema);
        };
        invoice_header.fee_type && changeForm(invoice_header.fee_type);
    }, [invoice_header.fee_type]);

    // Set Class, Division, Student Name, Other Ref, Parent Name, Debit Ledger Id Based on selected Student
    useEffect(() => {
        const setFewAttributeValues = (student_id) => {
            const student = dependencies.students.find(
                (stu) => stu.id === student_id
            );
            setInvoice((prevState) => {
                return {
                    ...prevState,
                    class_id: student.class_id,
                    division: student.division,
                    student_name: `${student.first_name} ${student.last_name}`,
                    parent_name: student.father_name,
                    other_ref: student.remarks,
                    debit_ledger_id: student.id,
                };
            });
        };
        const fetchActiveTransportByStudent = async (student_id) => {
            const response = await get(
                EntityConstants.STUDENT_TRANSPORT_DETAIL_BY_STUDENT + `/${student_id}`
            );
            const atd = await response.json();
            atd && setInvoice((prevState) => {
                return {
                    ...prevState,
                    zone_id: atd.zone_id,
                    transport_type_id: atd.transport_type_id,
                    invoice_start_date: atd.start_date,
                    invoice_end_date: atd.end_date,
                };
            });
        };
        dependencies.students.length > 0 &&
            invoice_header.student_id &&
            setFewAttributeValues(invoice_header.student_id);
        invoice_header.student_id &&
            fetchActiveTransportByStudent(invoice_header.student_id);
    }, [invoice_header.student_id, dependencies.students]);

    useEffect(() => {
        const fetchRespectiveAcademicYear = async () => {
            const response = await get(`${EntityConstants.ACADEMIC_YEAR_BY_TYPE}/Invoice/${invoice_header.student_id}/${invoice_header.id}`);
            const data = await response.json();
            setInvoice((prevState) => {
                return {
                    ...prevState,
                    academic_year_id: data.academic_year_id,
                };
            });
        };
        invoice_header.student_id && fetchRespectiveAcademicYear();
    }, [invoice_header.student_id, invoice_header.id]);

    // Attach schools as option
    useEffect(() => {
        if (dependencies.schools.length > 0) {
            attachOptions("school_id", dependencies.schools);
        }
    }, [dependencies.schools]);

    // Attach accounts as option
    useEffect(() => {
        if (dependencies.accounts.length > 0) {
            attachOptions("credit_ledger_id", dependencies.accounts);
        }
    }, [dependencies.accounts]);

    // Attach transport types as option
    useEffect(() => {
        if (dependencies.transport_types.length > 0) {
            attachOptions("transport_type_id", dependencies.transport_types);
        }
    }, [dependencies.transport_types]);

    // Attach student as option
    useEffect(() => {
        if (dependencies.students.length > 0) {
            attachOptions("student_id", dependencies.students);
            attachOptions("debit_ledger_id", dependencies.students);
        }
    }, [dependencies.students]);

    // Attach academic years as option
    useEffect(() => {
        if (dependencies.academic_years.length > 0) {
            attachOptions("academic_year_id", dependencies.academic_years);
            // invoiceHeaderChangeHandler(
            //     "academic_year_id",
            //     dependencies.academic_years[0].id
            // );
        }
    }, [dependencies.academic_years]);

    // Attach classes as option
    useEffect(() => {
        if (dependencies.classes.length > 0) {
            attachOptions("class_id", dependencies.classes);
        }
    }, [dependencies.classes]);

    // Attach zones as option
    useEffect(() => {
        if (dependencies.zones.length > 0) {
            attachOptions("zone_id", dependencies.zones);
        }
    }, [dependencies.zones]);

    // Attach terms as option
    useEffect(() => {
        if (dependencies.terms.length > 0) {
            attachOptions("term_id", dependencies.terms);
        }
    }, [dependencies.terms]);

    return (
        <Container maxWidth="xl">
            <Formik
                enableReinitialize
                initialValues={invoice_header}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({ values, touched, errors, handleChange, handleSubmit }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                // maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={3} lg={3}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => {
                                                    handleChange(e);
                                                    handleValueChange(e, values);
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {((values.id === undefined &&
                                    permissions.has(PermissionConstants.INVOICE_CREATE)) ||
                                    (values.id !== undefined &&
                                        permissions.has(PermissionConstants.INVOICE_UPDATE) &&
                                        invoiceStatus)) && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="eva:save-fill" />}
                                            type="submit"
                                        >
                                            {values.id === undefined ? "Submit" : "Update"}
                                        </Button>
                                    )}
                                &nbsp;&nbsp;
                                {values.id !== undefined &&
                                    invoice_header.fee_type === "Contract" &&
                                    permissions.has(PermissionConstants.INVOICE_DOWNLOAD) && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="typcn:download" />}
                                            onClick={handlePdfDownloadChange}
                                        >
                                            Download
                                        </Button>
                                    )}
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {(id !== undefined || invoiceId !== undefined) &&
                invoice_header.fee_type !== null && (
                    <InvoiceLines
                        invoiceHeader={invoice_header}
                        invoiceHeaderId={invoiceId}
                        invoiceLines={invoiceLines}
                        reload={() => getInvoiceLines(invoiceId)}
                    />
                )}
        </Container>
    );
};

Invoice.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default Invoice;
